import {
  Admin,
  CustomRoutes,
  ListGuesser,
  RaThemeOptions,
  Resource,
  defaultTheme,
  localStorageStore,
} from "react-admin";
import type {
  AdminProps,
  AuthProvider,
  DataProvider,
  TranslationMessages,
} from "react-admin";
import { deepmerge } from "@mui/utils";
import polyglotI18nProvider from "ra-i18n-polyglot";
import englishMessages from "ra-language-english";
import portugueseMessages from "./traducaoPortugues";

import { Route } from "react-router";

import Layout from "../layout/Layout";

import suppliers from "../suppliers";
import suppliercontacts from "../suppliercontacts";
import { Dashboard } from "../dashboard/Dashboard";
import purchaseorders from "../purchaseorders";
import { LoginPage } from "../login/LoginPage";
import scmanalyses from "../scmanalyses";
import scmskus from "../scmskus";
import skurankings from "../skurankings";
import scmprojectionlogs from "../scmprojectionlogs";
import skucmms from "../skucmms";

import {
  authProvider as defaultAuthProvider,
  dataProvider as defaultDataProvider,
} from "../providers/simplerest";
import buyers from "../buyers";
import scmconfigs from "../scmconfigs";
import notifications from "../notifications";
import { ProfilePage } from "../profiles/ProfilePage";
import {
  ConfigurationContextValue,
  ConfigurationProvider,
} from "./ConfigurationContext";
import {
  defaultCompanySectors,
  defaultContactGender,
  defaultDealPipelineStatuses,
  defaultDealStages,
  defaultLogo,
  defaultNoteStatuses,
  defaultPersonType,
  defaultTitle,
} from "./defaultConfiguration";

import { producao } from "../providers/simplerest/authProvider";

// Define the interface for the SCM component props
type SCMProps = {
  dataProvider?: DataProvider;
  authProvider?: AuthProvider;
  lightTheme?: RaThemeOptions;
  darkTheme?: RaThemeOptions;
} & Partial<ConfigurationContextValue> &
  Partial<AdminProps>;

const defaultLightTheme = deepmerge(defaultTheme, {
  palette: {
    background: {
      default: "#fafafb",
    },
    primary: {
      //-- não serve porque o PROD fica true quando faz o build, e só fica false no localhost
      //main: import.meta.env.PROD ? "#802539" : "#555555",
      main: producao ? "#802539" : "#555555",
      //--]
      //---
    },
  },
  components: {
    MuiAutocomplete: {
      defaultProps: {
        noOptionsText: "Nenhum resultado encontrado",
      },
    },
    RaFileInput: {
      styleOverrides: {
        root: {
          "& .RaFileInput-dropZone": {
            backgroundColor: "rgba(0, 0, 0, 0.04)",
          },
        },
      },
    },
  },
});

type translationType = {
  [key: string]: TranslationMessages;
};

const traducoes: translationType = {
  en: deepmerge(englishMessages, {
    menu: { skucmms: "CMM por loja" },
  }),
  pt: portugueseMessages,
};

export const i18nProvider = polyglotI18nProvider(
  (locale) => traducoes[locale],
  "pt", // default locale
  [
    { locale: "en", name: "English" },
    { locale: "pt", name: "Português" },
  ],
);

/**
 * SCM Component
 *
 * This component sets up and renders the main SCM application using `react-admin`. It provides
 * default configurations and themes but allows for customization through props. The component
 * wraps the application with a `ConfigurationProvider` to provide configuration values via context.
 *
 * @param {Array<ContactGender>} contactGender - The gender options for contacts used in the application.
 * @param {string[]} companySectors - The list of company sectors used in the application.
 * @param {RaThemeOptions} darkTheme - The theme to use when the application is in dark mode.
 * @param {string[]} dealCategories - The categories of deals used in the application.
 * @param {string[]} dealPipelineStatuses - The statuses of deals in the pipeline used in the application.
 * @param {DealStage[]} dealStages - The stages of deals used in the application.
 * @param {RaThemeOptions} lightTheme - The theme to use when the application is in light mode.
 * @param {string} logo - The logo used in the SCM application.
 * @param {NoteStatus[]} noteStatuses - The statuses of notes used in the application.
 * @param {string} title - The title of the SCM application.
 *
 * @returns {JSX.Element} The rendered SCM application.
 *
 */
export const SCM = ({
  contactGender = defaultContactGender,
  personType = defaultPersonType,
  companySectors = defaultCompanySectors,
  darkTheme,
  dealPipelineStatuses = defaultDealPipelineStatuses,
  dealStages = defaultDealStages,
  lightTheme = defaultLightTheme,
  logo = defaultLogo,
  noteStatuses = defaultNoteStatuses,
  title = defaultTitle,
  dataProvider = defaultDataProvider,
  authProvider = defaultAuthProvider,
}: SCMProps) => (
  <ConfigurationProvider
    contactGender={contactGender}
    personType={personType}
    companySectors={companySectors}
    dealPipelineStatuses={dealPipelineStatuses}
    dealStages={dealStages}
    logo={logo}
    noteStatuses={noteStatuses}
    title={title}
  >
    <Admin
      dataProvider={dataProvider}
      authProvider={authProvider}
      store={localStorageStore(undefined, "SCM")}
      layout={Layout}
      loginPage={LoginPage}
      dashboard={Dashboard}
      theme={lightTheme}
      darkTheme={darkTheme || null}
      i18nProvider={i18nProvider}
      requireAuth
      disableTelemetry
    >
      <CustomRoutes>
        <Route path={ProfilePage.path} element={<ProfilePage />} />
      </CustomRoutes>
      <Resource name="purchaseorders" {...purchaseorders} />
      <Resource name="suppliercontacts" {...suppliercontacts} />
      <Resource name="suppliers" {...suppliers} />
      <Resource name="persons" />
      <Resource name="customernotes" />
      <Resource name="dealnotes" />
      <Resource name="tasks" list={ListGuesser} />
      <Resource name="buyers" {...buyers} />
      <Resource name="scmtags" list={ListGuesser} />
      <Resource name="scmanalyses" {...scmanalyses} />
      <Resource name="scmskus" {...scmskus} />
      <Resource name="skurankings" {...skurankings} />
      <Resource name="skucmms" {...skucmms} />
      <Resource name="supplierissuers" />
      <Resource name="scmprojectionlogs" {...scmprojectionlogs} />
      <Resource name="scmconfigs" {...scmconfigs} />
      <Resource name="notifications" {...notifications} />
    </Admin>
  </ConfigurationProvider>
);
